import { Number, Array, Optional, Union, Literal, Boolean, Static, Object, String } from '@sinclair/typebox';

import { TravelPassApiError, TravelPassError } from './error';
import { AirportCode } from './travel-pass';

export const TravelPassReservationProductCode = Union([Literal('TPU'), Literal('TMP'), Literal('TPC'), Literal('TPP')]);
export type TravelPassReservationProductCode = Static<typeof TravelPassReservationProductCode>;

export const TravelPassReservationStatus = Object({
  reservationStatus: String(),
  isflown: Boolean(),
  isTicketReissued: Boolean(),
});
export type TravelPassReservationStatus = Static<typeof TravelPassReservationStatus>;

export const TravelPassReservationCodeName = Object({
  name: String(),
  code: String(),
});
export type TravelPassReservationCodeName = Static<typeof TravelPassReservationCodeName>;

export const TravelPassReservationTypeName = Object({
  name: String(),
  type: String(),
});
export type TravelPassReservationTypeName = Static<typeof TravelPassReservationTypeName>;

export const TravelPassReservationAirportDetails = Object({
  airportCode: AirportCode,
  airportName: String(),
  cityCode: String(),
  cityName: String(),
  countryCode: String(),
  countryName: String(),
  regionCode: String(),
  regionName: String(),
});
export type TravelPassReservationAirportDetails = Static<typeof TravelPassReservationAirportDetails>;

export const TravelPassReservationCarrier = Object({
  code: String(),
  name: String(),
  flightNumber: Optional(String()),
});
export type TravelPassReservationCarrier = Static<typeof TravelPassReservationCarrier>;

export const TravelPassReservationFlightSegment = Object({
  id: Number(),
  segmentKey: String(),
  bookingReference: String(),
  type: TravelPassReservationCodeName,
  status: String(),
  departure: TravelPassReservationAirportDetails,
  arrival: TravelPassReservationAirportDetails,
  duration: String(),
  scheduledArrivalLocal: String(),
  scheduledArrivalGMT: String(),
  scheduledDepartureLocal: String(),
  scheduledDepartureGMT: String(),
  checkInStartDateLocal: String(),
  checkInStartDateGMT: String(),
  checkInCloseDateLocal: String(),
  checkInCloseDateGMT: String(),
  boardingTimeLocal: Optional(String()),
  boardingTimeGMT: Optional(String()),
  operatingCarrier: TravelPassReservationCarrier,
  marketingCarrier: TravelPassReservationCarrier,
  aircraft: TravelPassReservationCodeName,
});
export type TravelPassReservationFlightSegment = Static<typeof TravelPassReservationFlightSegment>;

export const TravelPassReservationPrepaidAncillaries = Object({
  hasSeat: Boolean(),
  hasLounge: Boolean(),
  hasBaggage: Boolean(),
  hasMeal: Boolean(),
  hasBiofuel: Boolean(),
  baggage: Optional(
    Object({
      noOfRegularBaggages: Number(),
      noOfSpecialBaggages: Number(),
      noOfCabin: Number(),
      specialBaggages: Optional(
        Array(
          Object({
            type: String(),
            count: Number(),
          })
        )
      ),
    })
  ),
  lounge: Optional(Number()),
  meal: Optional(Number()),
  seat: Optional(Number()),
});
export type TravelPassReservationPrepaidAncillaries = Static<typeof TravelPassReservationPrepaidAncillaries>;

export const TravelPassReservationBoundConnection = Object({
  boundKey: String(),
  status: String(),
  origin: TravelPassReservationAirportDetails,
  destination: TravelPassReservationAirportDetails,
  startTimeLocal: String(),
  startTimeGMT: String(),
  endTimeLocal: String(),
  endTimeGMT: String(),
  duration: String(),
  segmentCount: Number(),
  bookingClass: String(),
  serviceClassName: Optional(String()),
  flightSegments: Array(TravelPassReservationFlightSegment),
  prepaidAncillaries: Optional(TravelPassReservationPrepaidAncillaries),
});
export type TravelPassReservationBoundConnection = Static<typeof TravelPassReservationBoundConnection>;

export const TravelPassReservationSpecialAssistance = Object({
  wheelChair: Optional(Array(TravelPassReservationTypeName)),
  serviceAnimal: Optional(Array(TravelPassReservationTypeName)),
  hearingAssistance: Optional(Array(TravelPassReservationTypeName)),
});
export type TravelPassReservationSpecialAssistance = Static<typeof TravelPassReservationSpecialAssistance>;

export const TravelPassReservationPassenger = Object({
  passengerKey: String(),
  id: Number(),
  firstName: String(),
  lastName: String(),
  fullName: String(),
  title: Optional(String()),
  paxKey: Number(),
  type: Optional(TravelPassReservationCodeName),
  gender: Optional(TravelPassReservationCodeName),
  email: Optional(String()),
  phone: Optional(String()),
  travelPassNumber: Optional(String()),
  bonusProgram: Optional(
    Object({
      bonusProgramKey: String(),
      loyaltyNumber: String(),
      tier: Optional(String()),
    })
  ),

  frequentFlyerNumber: Optional(String()),
  documentDetails: Object({
    number: String(),
    type: TravelPassReservationCodeName,
  }),
});
export type TravelPassReservationPassenger = Static<typeof TravelPassReservationPassenger>;

export const TravelPassReservationRequest = Object({
  pnr: String(),
  lastName: String(),
});
export type TravelPassReservationRequest = Static<typeof TravelPassReservationRequest>;

export const TravelPassReservationError = TravelPassApiError(TravelPassError);
export type TravelPassReservationError = Static<typeof TravelPassReservationError>;

export const TravelPassReservationResponse = Object({
  reservationId: String(),
  euroBonusNumber: Optional(String()),
  isShortHaul: Boolean(),
  airlineBookingReference: String(),
  pos: String(),
  createdDateLocal: String(),
  createdDateGMT: String(),
  passengerCount: Number(),
  connectionCount: Number(),
  category: TravelPassReservationCodeName,
  type: TravelPassReservationCodeName,
  tripType: TravelPassReservationCodeName,
  status: TravelPassReservationStatus,
  passengers: Array(TravelPassReservationPassenger),
  outbound: TravelPassReservationBoundConnection,
  inbound: Optional(TravelPassReservationBoundConnection),
  specialAssistance: Optional(TravelPassReservationSpecialAssistance),
  productCode: TravelPassReservationProductCode,
  eligibility: Object({
    outbound: Boolean(),
    inbound: Optional(Boolean()),
  }),
});
export type TravelPassReservationResponse = Static<typeof TravelPassReservationResponse>;
